import type { DeepPartial, LineType, PolygonType, Styles, TooltipShowRule } from 'klinecharts'
import { parseHslToken, hslToHex } from '@/features/theme-editor/color'
import { currentChartColorDefaults } from './chart-color-defaults'
import { readTextScale } from '@/features/theme-editor/text-scale'

// klinecharts' string enums, inlined as typed constants. This module loads
// eagerly (main.tsx → ChartThemeColorsProvider → buildChartTheme), and a
// VALUE import of the enums would put the entire klinecharts runtime into
// the entry bundle — same leak class as the '@edge/charts/interaction' fix.
// Literal values are pinned by the vendored klinecharts@9.8.12 dist.
const LINE_DASHED = 'dashed' as LineType
const LINE_SOLID = 'solid' as LineType
const POLYGON_FILL = 'fill' as PolygonType
const TOOLTIP_SHOW_NONE = 'none' as TooltipShowRule

// Market candle colors are now PER-THEME (dark/black/light differ) — see
// chart-color-defaults.ts. Sourced as EXACT hex rather than through
// `hsl(var(--chart-candle-*))`: the HSL token round-trips lossily
// (#22c55e -> "142 71% 45%" -> #21c45d), which desynced the canvas, the settings
// swatch highlight, and the follow-theme reset. `buildChartTheme` reads the
// active mode's pair live so a theme switch (which bumps themeVersion → rebuild)
// repaints the candles.

/** `#rrggbb` + alpha (0–1) -> `rgba(...)`. Pass-through for non-6-hex input. */
function hexToRgba(hex: string, alpha: number): string {
  const h = hex.replace(/^#/, '')
  if (h.length !== 6) return hex
  const n = parseInt(h, 16)
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`
}

/**
 * Read a CSS custom property value from :root.
 * Returns the raw string (e.g., "228 29% 14%" for HSL values).
 */
function getCSSVar(name: string): string {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim()
}

/** Convert an "H S% L%" token value to a concrete #hex, or pass through. */
function tokenToHex(value: string): string {
  try {
    return hslToHex(parseHslToken(value))
  } catch {
    return value
  }
}

/**
 * Resolve a CSS custom property to a CONCRETE hex color.
 *
 * We deliberately return hex (not an `hsl(...)` string) because these colors
 * flow onto the chart <canvas> AND through color-math utilities
 * (scaleBrightness for the volume overlay, hexToRgba for customized candles)
 * that only parse hex/rgb — an `hsl()` string silently fails there.
 */
function hsl(varName: string): string {
  const value = getCSSVar(varName)
  if (!value) return ''
  return tokenToHex(value)
}

/**
 * Concrete font stack for every text style in the theme — resolved from
 * `--font-sans` so the chart tracks the app font, with the static stack as the
 * fallback (jsdom, or a theme missing the var).
 *
 * NEVER put `'inherit'` (or any CSS-wide keyword) in a `family`/`textFamily`
 * field: klinecharts concatenates it into `ctx.font = "normal 12px inherit"`,
 * which is an INVALID canvas font string — the assignment is silently ignored
 * and the text renders with whatever font the PREVIOUS draw call left on the
 * context. Symptoms: labels that change size between paints as draw order
 * shifts (e.g. y-axis ticks growing after the axis-width tier kicks in), and
 * mis-measured label boxes. Found the hard way, twice (label-box slack on the
 * last-price label; axis-tick font jumping after load, Chris 2026-07-30).
 */
function fontFamily(): string {
  return getCSSVar('--font-sans') || "'IBM Plex Sans', ui-sans-serif, system-ui, sans-serif"
}

/**
 * Resolve a CSS custom property to a CONCRETE rgba() string with alpha.
 */
function hsla(varName: string, alpha: number): string {
  const value = getCSSVar(varName)
  if (!value) return ''
  const hex = tokenToHex(value).replace(/^#/, '')
  if (hex.length !== 6) return ''
  const n = parseInt(hex, 16)
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`
}

/**
 * Build a KlineCharts DeepPartial<Styles> from CSS custom properties.
 *
 * This allows the chart to inherit colors from the app's theme system
 * (light/dark) without hardcoding colors. Called when:
 * - A chart mounts
 * - The theme toggles (light ↔ dark)
 *
 * Maps CSS variables from index.css:
 * --chart-grid, --chart-crosshair, --chart-tooltip-bg,
 * --chart-candle-up, --chart-candle-down,
 * --background, --foreground, --border, --muted-foreground
 */
export function buildChartTheme(): DeepPartial<Styles> {
  const fg = hsl('--foreground')
  const muted = hsl('--muted-foreground')
  // Doji (no-change) last-price pill fill: a fixed darker gray. --muted-foreground
  // is too light under the pill's white text (especially in light themes), so the
  // "5.80" reads low-contrast on a Doji bar. Darker gray keeps it legible.
  const lastNoChange = '#4b5563'
  const border = hsl('--border')
  const bg = hsl('--background')
  const gridColor = hsl('--chart-grid')
  const crosshairColor = hsl('--chart-crosshair')
  const tooltipBg = hsl('--chart-tooltip-bg')
  const { candleUp, candleDown } = currentChartColorDefaults()
  const family = fontFamily()
  // Text sizes follow the text-size setting (canvas text never sees CSS); line
  // widths, which klinecharts also calls `size`, do not. setTextScale bumps
  // themeVersion, which rebuilds this theme.
  const scale = readTextScale()
  const text = (px: number) => Math.round(px * scale * 10) / 10

  // Axis tick labels (x-axis dates, y-axis price ticks). In the Edge Dark theme
  // SPECIFICALLY, dim the default muted-foreground text to 80% opacity so the
  // axis reads a touch softer against the dark surface (Chris 2026-08-08). Same
  // theme-identity hook the mosaic gradient uses (data-theme-id='dark', set in
  // theme-runtime.applyThemeOption). Every other theme keeps full-opacity axis
  // text — `muted` unchanged. themeVersion in SettingsContext forces a repaint
  // when the theme switches, so this re-evaluates on toggle.
  const isEdgeDark = document.documentElement.dataset.themeId === 'dark'
  const axisText = isEdgeDark ? hsla('--muted-foreground', 0.8) : muted

  return {
    grid: {
      show: true,
      horizontal: {
        show: true,
        size: 1,
        color: gridColor,
        style: LINE_DASHED,
        dashedValue: [2, 2],
      },
      vertical: {
        show: true,
        size: 1,
        color: gridColor,
        style: LINE_DASHED,
        dashedValue: [2, 2],
      },
    },
    candle: {
      priceMark: {
        show: true,
        high: {
          show: true,
          color: fg,
          textSize: text(10),
          textFamily: family,
          textWeight: 'normal',
          textOffset: 5,
        },
        low: {
          show: true,
          color: fg,
          textSize: text(10),
          textFamily: family,
          textWeight: 'normal',
          textOffset: 5,
        },
        last: {
          show: true,
          upColor: candleUp,
          downColor: candleDown,
          noChangeColor: lastNoChange,
          line: { show: true, style: LINE_DASHED, dashedValue: [4, 4], size: 1 },
          text: {
            show: true,
            style: POLYGON_FILL,
            size: text(11),
            paddingLeft: 4,
            paddingTop: 2,
            paddingRight: 4,
            paddingBottom: 2,
            borderStyle: LINE_SOLID,
            borderSize: 0,
            borderDashedValue: [2, 2],
            color: '#ffffff',
            family,
            weight: 'normal',
            borderRadius: 2,
          },
        },
      },
      bar: {
        upColor: candleUp,
        downColor: candleDown,
        noChangeColor: muted,
        upBorderColor: candleUp,
        downBorderColor: candleDown,
        noChangeBorderColor: muted,
        upWickColor: candleUp,
        downWickColor: candleDown,
        noChangeWickColor: muted,
      },
      tooltip: {
        offsetTop: 4,
        text: { marginLeft: 4 },
      },
    },
    indicator: {
      lastValueMark: { show: false },
      // Native indicator tooltip fully suppressed — the React IndicatorLegend
      // renders the values. `showRule` is load-bearing: klinecharts defaults it
      // to 'always', and `ChartManager.setTheme` passes a theme OBJECT straight
      // through (only the 'dark'/'light' STRINGS fall back to the package
      // bundles), so omitting it here does not inherit darkTheme's 'none' — it
      // reverts to the library default. That double-printed the canvas row over
      // the legend in every SUB-pane (main-pane indicators were spared by
      // ChartManager.createIndicator's tooltip-data-source stub), e.g. VOL in
      // its own pane showed "Vol: …" twice, overlapping.
      tooltip: { showRule: TOOLTIP_SHOW_NONE, showName: false, showParams: false },
      bars: [
        {
          upColor: hexToRgba(candleUp, 0.7),
          downColor: hexToRgba(candleDown, 0.7),
          noChangeColor: muted,
        },
      ],
      circles: [
        {
          upColor: hexToRgba(candleUp, 0.7),
          downColor: hexToRgba(candleDown, 0.7),
          noChangeColor: muted,
        },
      ],
      ohlc: {
        upColor: candleUp,
        downColor: candleDown,
        noChangeColor: muted,
      },
    },
    // Tick size 10 on BOTH axes — not 12. Before the font-family fix (see
    // fontFamily()) the declared 12 was never actually rendered: the invalid
    // 'inherit' family voided the assignment and first-paint ticks drew with
    // the canvas DEFAULT font (10px). That 10px look is what the app always
    // shipped and what the design settled on (Chris, 2026-07-30) — so 10 is
    // the intended size, now rendered deliberately instead of by accident.
    xAxis: {
      axisLine: { show: true, color: border, size: 1 },
      tickText: { show: true, color: axisText, size: text(10), family, weight: 'normal' },
      tickLine: { show: true, size: 1, length: 3, color: border },
    },
    yAxis: {
      axisLine: { show: true, color: border, size: 1 },
      tickText: { show: true, color: axisText, size: text(10), family, weight: 'normal' },
      tickLine: { show: true, size: 1, length: 3, color: border },
    },
    crosshair: {
      show: true,
      horizontal: {
        show: true,
        line: {
          show: true,
          style: LINE_DASHED,
          dashedValue: [4, 2],
          size: 1,
          color: crosshairColor,
        },
        text: {
          show: true,
          style: POLYGON_FILL,
          color: fg,
          size: text(11),
          family,
          weight: 'normal',
          borderStyle: LINE_SOLID,
          borderDashedValue: [2, 2],
          borderSize: 0,
          borderColor: 'transparent',
          borderRadius: 2,
          paddingLeft: 4,
          paddingRight: 4,
          paddingTop: 2,
          paddingBottom: 2,
          backgroundColor: tooltipBg,
        },
      },
      vertical: {
        show: true,
        line: {
          show: true,
          style: LINE_DASHED,
          dashedValue: [4, 2],
          size: 1,
          color: crosshairColor,
        },
        text: {
          show: true,
          style: POLYGON_FILL,
          color: fg,
          size: text(11),
          family,
          weight: 'normal',
          borderStyle: LINE_SOLID,
          borderDashedValue: [2, 2],
          borderSize: 0,
          borderColor: 'transparent',
          borderRadius: 2,
          paddingLeft: 4,
          paddingRight: 4,
          paddingTop: 2,
          paddingBottom: 2,
          backgroundColor: tooltipBg,
        },
      },
    },
    separator: {
      size: 1,
      color: border,
      fill: true,
      activeBackgroundColor: hsla('--primary', 0.15),
    },
    overlay: {
      // Draggable endpoint handles for drawings. Klinecharts' default is a small
      // dot with a faint 35%-alpha ring that blends into the line it anchors.
      // We give each handle a larger disc ringed with the chart BACKGROUND so it
      // reads as a distinct puck detached from the thin line — and grow it on
      // hover/grab for clear drag feedback.
      point: {
        radius: 6,
        borderColor: bg,
        borderSize: 2,
        activeRadius: 8,
        activeBorderColor: bg,
        activeBorderSize: 3,
      },
    },
  }
}

/**
 * Detect current theme mode from the document's class list.
 */
export function getCurrentThemeMode(): 'dark' | 'light' {
  const cl = document.documentElement.classList
  return cl.contains('dark') || cl.contains('black') ? 'dark' : 'light'
}
